/**
 * MarketingTab — the listing's Marketing tab (listing-marketing-hub US-2 – US-8, BR-1, BR-11, BR-12).
 *
 * A status banner (Not marketed yet / N posts live / paused / closed), the recorded posts with
 * Open post, Mark removed and Delete (the external-posts UI that used to sit in "Advertise on other
 * sites"), and a channel rail with one panel at a time: Craigslist, Facebook Marketplace, Other
 * sites. The default channel is the first tracked site with no posted record, fixed when the posts
 * first load so recording a post does not move the landlord to another guide.
 *
 * Only an active listing can be posted: on a paused or closed one the Post actions are replaced by a
 * note, the channel panels' posting and copy controls are inert (the guide text, the Help link and
 * the disclosures stay readable and usable), and the still-live posts lead the tab with the
 * take-down list (BR-1, BR-15).
 */
import { useState, type ReactNode } from 'react';
import { ExternalLink, MessageCircle, Monitor } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUnitTimezone } from '../../../hooks/useUnitTimezone';
import { useListingExternalPosts } from '../../../hooks/useListingExternalPosts';
import { unitPhotosQueryOptions } from '../../../hooks/useUnitPhotos';
import {
  deleteExternalPost,
  externalPostsKey,
  LISTING_SITE_LABELS,
  markExternalPostRemoved,
  type ExternalListingPostDto,
  type ListingSiteDto,
} from '../../../lib/external-listing-posts.api';
import { detectPostCapability, startPost, type PostCapability } from '../../../lib/listing-extension-bridge';
import { downloadPhotosArchive, fetchMarketingExport, marketingExportKey } from '../../../lib/listing-marketing.api';
import { isMarketed, postedPosts } from '../../../lib/listing-marketing';
import { fetchUnits, type Listing } from '../../../lib/leasing.api';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { ExternalPostsPanel } from '../ExternalPostsPanel';
import { InstallExtensionDialog } from '../InstallExtensionDialog';
import { LISTING_STATUS_LABELS } from '../listing-status';
import { ManualPostForm } from '../ManualPostForm';
import { ChannelRail, type ChannelRailItem, type MarketingChannel } from './ChannelRail';
import { CraigslistPanel } from './CraigslistPanel';
import { FacebookPanel } from './FacebookPanel';
import { MarketingStatusBanner, type MarketingBannerState } from './MarketingStatusBanner';
import { OtherSitesPanel } from './OtherSitesPanel';
import { Callout } from './StepList';
import { formatUnitAddress } from '@resida/shared';

interface MarketingTabProps {
  listing: Listing;
  /** The listing's Resida email address (its intake alias). */
  intakeAddress: string | null;
  /** Re-activates a paused listing; the paused banner links to it. */
  onReactivate?: () => void;
}

const CHANNEL_SITE: Record<Exclude<MarketingChannel, 'other'>, ListingSiteDto> = {
  craigslist: 'craigslist',
  facebook: 'facebook_marketplace',
};

const POST_TEST_IDS: Record<ListingSiteDto, string> = {
  facebook_marketplace: 'post-to-facebook',
  craigslist: 'post-to-craigslist',
};

/** BR-27 copy for browsers that cannot run the extension. */
const UNSUPPORTED_NOTE: Record<'unsupported_touch' | 'unsupported_desktop', { title: string; body: string }> = {
  unsupported_touch: {
    title: 'Post faster from a computer',
    body: 'Filling Facebook Marketplace and Craigslist forms works on a computer, in Chrome with the Resida extension. Here you can copy the text, download the photos and record your posts.',
  },
  unsupported_desktop: {
    title: 'Post faster in Chrome',
    body: 'Filling Facebook Marketplace and Craigslist forms works in Chrome with the Resida extension. Open Resida in Chrome to use it, or post by hand and record the post here.',
  },
};

/** The first tracked channel with no posted record; Other sites once both have one. */
function firstUnpostedChannel(posted: ExternalListingPostDto[]): MarketingChannel {
  const hasPost = (site: ListingSiteDto) => posted.some((p) => p.site === site);
  return !hasPost('craigslist') ? 'craigslist' : !hasPost('facebook_marketplace') ? 'facebook' : 'other';
}

export function MarketingTab({ listing, intakeAddress, onReactivate }: MarketingTabProps) {
  const queryClient = useQueryClient();
  const isActive = listing.status === 'active';
  const [installSite, setInstallSite] = useState<ListingSiteDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MarketingChannel | null>(null);
  // The channel to show until the landlord picks one: decided once, when the posts first load.
  const [defaultChannel, setDefaultChannel] = useState<MarketingChannel | null>(null);

  const { data: capability } = useQuery<PostCapability>({
    queryKey: ['listing-extension', 'capability'],
    queryFn: detectPostCapability,
    enabled: isActive,
  });
  const postsQuery = useListingExternalPosts(listing.id);
  if (defaultChannel === null && postsQuery.data) setDefaultChannel(firstUnpostedChannel(postedPosts(postsQuery.data)));
  const kitQuery = useQuery({
    queryKey: marketingExportKey(listing.id),
    queryFn: () => fetchMarketingExport(listing.id),
  });
  const { data: unitPhotos } = useQuery(unitPhotosQueryOptions(listing.unitId));
  const { data: unitsData } = useQuery({ queryKey: ['units'], queryFn: () => fetchUnits() });
  const { data: timezone } = useUnitTimezone(listing.unitId);

  const invalidatePosts = () => queryClient.invalidateQueries({ queryKey: externalPostsKey(listing.id) });
  const markRemoved = useMutation({
    mutationFn: markExternalPostRemoved,
    onSuccess: invalidatePosts,
    onError: () => setActionError("Couldn't mark the post removed. Try again."),
  });
  const deletePost = useMutation({
    mutationFn: deleteExternalPost,
    onSuccess: invalidatePosts,
    onError: () => setActionError("Couldn't delete the post. Try again."),
  });

  const handlePost = async (site: ListingSiteDto) => {
    setActionError(null);
    if (capability === 'installable') {
      setInstallSite(site);
      return;
    }
    try {
      await startPost(listing.id, site);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Couldn't start the post. Try again.");
    }
  };

  if (postsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="marketing-tab-loading">
        <Skeleton className="h-24 w-full" />
        <div className="flex flex-col gap-4 md:flex-row">
          <Skeleton className="h-48 w-full md:w-60" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const posts = postsQuery.data ?? [];
  const posted = postedPosts(posts);
  // While the posts list cannot load, the listing's own summary still says whether it is marketed.
  const postedCount = postsQuery.isError ? (listing.marketing?.postedCount ?? 0) : posted.length;
  const sitePostedCount = (site: ListingSiteDto) => posted.filter((p) => p.site === site).length;
  const bannerState: MarketingBannerState = isActive
    ? isMarketed(postedCount)
      ? 'marketed'
      : 'not_marketed'
    : listing.status === 'closed'
      ? 'closed'
      : 'paused';
  const sitesPosted = (Object.values(CHANNEL_SITE) as ListingSiteDto[]).filter((s) => sitePostedCount(s) > 0).length;

  const channel = selected ?? defaultChannel ?? 'craigslist';

  const railItems: ChannelRailItem[] = [
    {
      key: 'craigslist',
      label: 'Craigslist',
      posted: sitePostedCount('craigslist') > 0,
      status: sitePostedCount('craigslist') > 0 ? 'Posted' : 'Not posted',
    },
    {
      key: 'facebook',
      label: 'Facebook Marketplace',
      posted: sitePostedCount('facebook_marketplace') > 0,
      status: sitePostedCount('facebook_marketplace') > 0 ? 'Posted' : 'Not posted',
    },
    { key: 'other', label: 'Other sites', posted: false, status: 'Copy and photos' },
  ];

  const statusWord = LISTING_STATUS_LABELS[listing.status].toLowerCase();

  const postAction = (site: ListingSiteDto): ReactNode => {
    if (!isActive) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="marketing-posting-off">
          Posting is off while this listing is {statusWord}.
        </p>
      );
    }
    if (capability === undefined) return <Skeleton className="h-11 w-full sm:w-72" />;
    if (capability === 'installed' || capability === 'installable') {
      const testId = POST_TEST_IDS[site];
      return (
        <div>
          <Button type="button" onClick={() => void handlePost(site)} data-testid={testId} className="w-full sm:w-auto">
            Post to {LISTING_SITE_LABELS[site]}
            <ExternalLink aria-hidden="true" />
          </Button>
        </div>
      );
    }
    return (
      <Callout icon={Monitor} title={UNSUPPORTED_NOTE[capability].title} testId="desktop-only-note">
        {UNSUPPORTED_NOTE[capability].body}
      </Callout>
    );
  };
  const recordForm = (site: ListingSiteDto): ReactNode =>
    isActive ? <ManualPostForm listingId={listing.id} site={site} /> : null;

  const unit = (unitsData?.units ?? []).find((u) => u.id === listing.unitId);
  const address = unit
    ? formatUnitAddress(unit.address1, unit.unitNumber)
    : '';
  const approvedPhotos = unitPhotos
    ?.filter((p) => p.status === 'approved' && p.url)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const busyPostId =
    (markRemoved.isPending ? markRemoved.variables : null) ?? (deletePost.isPending ? deletePost.variables : null) ?? null;

  const postsBlock = postsQuery.isError ? (
    <div
      role="alert"
      data-testid="external-posts-error"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
    >
      <p className="text-destructive">Couldn&apos;t load where this listing is posted.</p>
      <Button type="button" variant="outline" onClick={() => void postsQuery.refetch()} data-testid="external-posts-retry">
        Retry
      </Button>
    </div>
  ) : posts.length > 0 ? (
    <ExternalPostsPanel
      posts={posts}
      listingActive={isActive}
      listingStatusLabel={statusWord}
      timezone={timezone}
      busyPostId={busyPostId}
      onMarkRemoved={(postId) => {
        setActionError(null);
        markRemoved.mutate(postId);
      }}
      onDelete={(postId) => {
        setActionError(null);
        deletePost.mutate(postId);
      }}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-5" data-testid="advertise-section">
      <MarketingStatusBanner
        state={bannerState}
        postedCount={postedCount}
        sitesPosted={sitesPosted}
        onReactivate={listing.status === 'paused' ? onReactivate : undefined}
      />

      {actionError && (
        <p role="alert" data-testid="advertise-error" className="text-sm text-destructive">
          {actionError}
        </p>
      )}

      {postsBlock}

      {!isActive && (
        <>
          <p
            className="flex items-start gap-2 text-sm text-muted-foreground"
            data-testid="marketing-messenger-not-covered"
          >
            <MessageCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            {listing.status === 'paused'
              ? 'Replies that reach you in Facebook Messenger are not covered by the automatic reply. Tell those renters it is on hold.'
              : 'Resida cannot reply in Facebook Messenger. Tell renters who message you there that the place is no longer available.'}
          </p>
          {/* Posts made by hand can still be recorded so the take-down reminder covers them. */}
          <div className="rounded-xl border border-border bg-card p-[var(--card-padding)]">
            <ManualPostForm listingId={listing.id} />
          </div>
        </>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <ChannelRail items={railItems} active={channel} onSelect={setSelected} />
        <div className="flex min-w-0 flex-1" data-testid="marketing-channel-panel" data-posting-off={!isActive || undefined}>
          {channel === 'craigslist' && (
            <CraigslistPanel
              intakeAddress={intakeAddress}
              postAction={postAction('craigslist')}
              recordForm={recordForm('craigslist')}
              postingOff={!isActive}
              postedCount={sitePostedCount('craigslist')}
            />
          )}
          {channel === 'facebook' && (
            <FacebookPanel
              publicUrl={listing.publicUrl ?? null}
              intakeAddress={intakeAddress}
              address={address}
              postAction={postAction('facebook_marketplace')}
              recordForm={recordForm('facebook_marketplace')}
              postingOff={!isActive}
              postedCount={sitePostedCount('facebook_marketplace')}
            />
          )}
          {channel === 'other' && (
            <OtherSitesPanel
              kit={kitQuery.data}
              kitError={kitQuery.isError}
              onRetryKit={() => void kitQuery.refetch()}
              photos={approvedPhotos}
              onDownloadPhotos={() => downloadPhotosArchive(listing.id)}
              postingOff={!isActive}
            />
          )}
        </div>
      </div>

      <InstallExtensionDialog
        isOpen={installSite !== null}
        onClose={() => setInstallSite(null)}
        site={installSite ?? 'facebook_marketplace'}
      />
    </div>
  );
}
